import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getManagementDashboardAlerts,
  getManagementDashboardAcquisitionBreakdown,
  getManagementDashboardAnalytics,
  getManagementDashboardContractorPerformance,
  getManagementDashboardCriticalCases,
  getManagementDashboardExecutiveKpis,
  getManagementDashboardFunnel,
  getManagementDashboardLendingOverview,
  getManagementDashboardLoanPartnerPerformance,
  getManagementDashboardMehrsanOverview,
  getManagementDashboardOperationalBottlenecks,
  getManagementDashboardOperationalReadiness,
  getManagementDashboardRequestRiskPredictions,
  getManagementDashboardSlaPerformance,
  getManagementDashboardHealthScore,
  getManagementDashboardActionCenter,
  getManagementDashboardActions,
  getManagementDashboardExecutiveDailyBrief,
  getManagementDashboardBenchmarkComparison,
  syncManagementDashboardActions,
  updateManagementDashboardAction,
  getManagementDashboardStageAging,
  getManagementDashboardTrends,
  getManagementDashboardAccess,
} from "../../../../../../infrastructure/api/managementDashboard";
import {
  DEFAULT_GROWTH_PERIOD_DAYS,
  extractGrowthAnalyticsPayload,
  normalizeGrowthAnalytics,
  normalizeOverview,
  normalizeSmsSummary,
} from "../../../components/growthAnalyticsConstants";
import { TREND_METRIC_GROUPS } from "../ManagementTrendSection";
import {
  DASHBOARD_CACHE_TTL_MS,
  DEFAULT_MANAGEMENT_TAB,
  MANAGEMENT_DASHBOARD_TABS,
  MANAGEMENT_TAB_LOADER_KEYS,
} from "../managementDashboardConstants";
import { buildClientDashboardAccess } from "../managementDashboardAccess";
import {
  DASHBOARD_BOOTSTRAP_DELAY_MS,
  delay,
  fetchDashboardGet,
  filtersToApiQuery,
  getApiErrorMessage,
  isApiSuccess,
  normalizeExecutiveKpis,
  parseSearchParamsToFilters,
  pick,
} from "../managementDashboardUtils";

const useManagementDashboardData = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiError, setKpiError] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState(null);
  const [readinessData, setReadinessData] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnelError, setFunnelError] = useState(null);
  const [lendingLoading, setLendingLoading] = useState(false);
  const [lendingError, setLendingError] = useState(null);
  const [lendingData, setLendingData] = useState(null);
  const [mehrsanLoading, setMehrsanLoading] = useState(false);
  const [mehrsanError, setMehrsanError] = useState(null);
  const [mehrsanData, setMehrsanData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);
  const [growthAnalytics, setGrowthAnalytics] = useState(null);
  const [smsSummary, setSmsSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [executiveKpis, setExecutiveKpis] = useState([]);
  const [funnelStages, setFunnelStages] = useState([]);
  const [funnelMeta, setFunnelMeta] = useState({ cohortSize: 0, excludedCancelledCount: 0 });
  const [trendSeries, setTrendSeries] = useState([]);
  const [acquisitionLoading, setAcquisitionLoading] = useState(false);
  const [acquisitionError, setAcquisitionError] = useState(null);
  const [acquisitionData, setAcquisitionData] = useState(null);
  const [stageAgingLoading, setStageAgingLoading] = useState(false);
  const [stageAgingError, setStageAgingError] = useState(null);
  const [stageAgingData, setStageAgingData] = useState(null);
  const [criticalCasesLoading, setCriticalCasesLoading] = useState(false);
  const [criticalCasesError, setCriticalCasesError] = useState(null);
  const [criticalCasesData, setCriticalCasesData] = useState(null);
  const [contractorPerfLoading, setContractorPerfLoading] = useState(false);
  const [contractorPerfError, setContractorPerfError] = useState(null);
  const [contractorPerfData, setContractorPerfData] = useState(null);
  const [loanPartnerPerfLoading, setLoanPartnerPerfLoading] = useState(false);
  const [loanPartnerPerfError, setLoanPartnerPerfError] = useState(null);
  const [loanPartnerPerfData, setLoanPartnerPerfData] = useState(null);
  const [bottlenecksLoading, setBottlenecksLoading] = useState(false);
  const [bottlenecksError, setBottlenecksError] = useState(null);
  const [bottlenecksData, setBottlenecksData] = useState(null);
  const [slaLoading, setSlaLoading] = useState(false);
  const [slaError, setSlaError] = useState(null);
  const [slaData, setSlaData] = useState(null);
  const [healthScoreLoading, setHealthScoreLoading] = useState(true);
  const [healthScoreError, setHealthScoreError] = useState(null);
  const [healthScoreData, setHealthScoreData] = useState(null);
  const [actionCenterLoading, setActionCenterLoading] = useState(false);
  const [actionCenterError, setActionCenterError] = useState(null);
  const [actionCenterData, setActionCenterData] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskError, setRiskError] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowError, setWorkflowError] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [workflowSyncing, setWorkflowSyncing] = useState(false);
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefError, setBriefError] = useState(null);
  const [briefData, setBriefData] = useState(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(true);
  const [benchmarkError, setBenchmarkError] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [benchmarkDimension, setBenchmarkDimension] = useState("Province");
  const [dashboardAccess, setDashboardAccess] = useState(() => buildClientDashboardAccess(null));
  const [currencyUnit, setCurrencyUnit] = useState("IRR");
  const [periodDays, setPeriodDays] = useState(DEFAULT_GROWTH_PERIOD_DAYS);
  const [isBootstrapReady, setIsBootstrapReady] = useState(false);
  const loadSeqRef = useRef(0);
  const loadedTabsRef = useRef(new Set());
  const cacheRef = useRef(new Map());
  const loadedKeysRef = useRef(new Set());

  const handleBootstrapReady = useCallback(() => {
    setIsBootstrapReady(true);
  }, []);

  useEffect(() => {
    const fallbackTimer = window.setTimeout(() => {
      setIsBootstrapReady((ready) => ready || true);
    }, 2500);
    return () => window.clearTimeout(fallbackTimer);
  }, []);

  const metricGroup = searchParams.get("trendGroup") || "requests";
  const selectedTrendMetric = searchParams.get("trendMetric") || "";

  const dashboardFilters = useMemo(
    () => parseSearchParamsToFilters(searchParams),
    [searchParams]
  );

  const apiFilter = useCallback((filters) => filtersToApiQuery(filters), []);

  const loadAlerts = useCallback(async (filters) => {
    try {
      setAlertsLoading(true);
      setAlertsError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardAlerts(apiFilter(filters))
      );
      if (isApiSuccess(res)) setAlertsData(res.data);
      else {
        setAlertsData(null);
        setAlertsError(getApiErrorMessage(res, "دریافت هشدارها ناموفق بود."));
      }
    } catch {
      setAlertsData(null);
      setAlertsError("خطا در دریافت هشدارهای مدیریتی.");
    } finally {
      setAlertsLoading(false);
    }
  }, [apiFilter]);

  const loadReadiness = useCallback(async (filters) => {
    try {
      setReadinessLoading(true);
      setReadinessError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardOperationalReadiness(apiFilter(filters))
      );
      if (isApiSuccess(res)) setReadinessData(res.data);
      else {
        setReadinessData(null);
        setReadinessError(getApiErrorMessage(res, "دریافت آمادگی عملیاتی ناموفق بود."));
      }
    } catch {
      setReadinessData(null);
      setReadinessError("خطا در دریافت آمادگی عملیاتی.");
    } finally {
      setReadinessLoading(false);
    }
  }, [apiFilter]);

  const loadLending = useCallback(async (filters) => {
    try {
      setLendingLoading(true);
      setLendingError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardLendingOverview(apiFilter(filters))
      );
      if (isApiSuccess(res)) setLendingData(res.data);
      else {
        setLendingData(null);
        setLendingError(getApiErrorMessage(res, "دریافت نمای وام ناموفق بود."));
      }
    } catch {
      setLendingData(null);
      setLendingError("خطا در دریافت نمای وام.");
    } finally {
      setLendingLoading(false);
    }
  }, [apiFilter]);

  const loadMehrsan = useCallback(async (filters) => {
    try {
      setMehrsanLoading(true);
      setMehrsanError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardMehrsanOverview(apiFilter(filters))
      );
      if (isApiSuccess(res)) setMehrsanData(res.data);
      else {
        setMehrsanData(null);
        setMehrsanError(getApiErrorMessage(res, "دریافت نمای مهرسان ناموفق بود."));
      }
    } catch {
      setMehrsanData(null);
      setMehrsanError("خطا در دریافت نمای مهرسان.");
    } finally {
      setMehrsanLoading(false);
    }
  }, [apiFilter]);

  const loadAnalytics = useCallback(async (days = DEFAULT_GROWTH_PERIOD_DAYS) => {
    try {
      setAnalyticsLoading(true);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardAnalytics(days)
      );
      if (isApiSuccess(res) && res.data) {
        const payload = extractGrowthAnalyticsPayload(res.data);
        setGrowthAnalytics(normalizeGrowthAnalytics(payload || res.data));
        setSmsSummary(normalizeSmsSummary(res.data));
        setOverview(normalizeOverview(res.data));
      } else {
        setGrowthAnalytics(null);
        setSmsSummary(null);
        setOverview(null);
      }
    } catch {
      setGrowthAnalytics(null);
      setSmsSummary(null);
      setOverview(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadExecutiveKpis = useCallback(async (filters) => {
    try {
      setKpiLoading(true);
      setKpiError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardExecutiveKpis(apiFilter(filters))
      );
      if (isApiSuccess(res) && (res.data ?? res.Data)) {
        const normalized = normalizeExecutiveKpis(res.data ?? res.Data);
        setExecutiveKpis(normalized.kpis);
        setCurrencyUnit(normalized.currencyUnit);
      } else {
        setExecutiveKpis([]);
        setKpiError(getApiErrorMessage(res, "دریافت شاخص‌های اجرایی ناموفق بود."));
      }
    } catch {
      setExecutiveKpis([]);
      setKpiError("خطا در ارتباط با سرور هنگام دریافت شاخص‌های اجرایی.");
    } finally {
      setKpiLoading(false);
    }
  }, [apiFilter]);

  const loadFunnel = useCallback(async (filters) => {
    try {
      setFunnelLoading(true);
      setFunnelError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardFunnel(apiFilter(filters))
      );
      if (isApiSuccess(res) && res.data) {
        setFunnelStages(pick(res.data, "stages", "Stages") || []);
        setFunnelMeta({
          cohortSize: pick(res.data, "cohortSize", "CohortSize") || 0,
          excludedCancelledCount:
            pick(res.data, "excludedCancelledCount", "ExcludedCancelledCount") || 0,
        });
      } else {
        setFunnelStages([]);
        setFunnelError(getApiErrorMessage(res, "دریافت قیف درخواست‌ها ناموفق بود."));
      }
    } catch {
      setFunnelStages([]);
      setFunnelError("خطا در دریافت قیف درخواست‌ها.");
    } finally {
      setFunnelLoading(false);
    }
  }, [apiFilter]);

  const loadTrends = useCallback(async (filters) => {
    try {
      setTrendLoading(true);
      setTrendError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardTrends(apiFilter(filters))
      );
      if (isApiSuccess(res) && res.data) {
        setTrendSeries(pick(res.data, "series", "Series") || []);
      } else {
        setTrendSeries([]);
        setTrendError(getApiErrorMessage(res, "دریافت روند شاخص‌ها ناموفق بود."));
      }
    } catch {
      setTrendSeries([]);
      setTrendError("خطا در دریافت روند شاخص‌ها.");
    } finally {
      setTrendLoading(false);
    }
  }, [apiFilter]);

  const loadAcquisitionBreakdown = useCallback(async (filters) => {
    try {
      setAcquisitionLoading(true);
      setAcquisitionError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardAcquisitionBreakdown(apiFilter(filters))
      );
      if (isApiSuccess(res)) setAcquisitionData(res.data);
      else {
        setAcquisitionData(null);
        setAcquisitionError(getApiErrorMessage(res, "دریافت تفکیک جذب ناموفق بود."));
      }
    } catch {
      setAcquisitionData(null);
      setAcquisitionError("خطا در دریافت تفکیک جذب متقاضی.");
    } finally {
      setAcquisitionLoading(false);
    }
  }, [apiFilter]);

  const loadStageAging = useCallback(async (filters) => {
    try {
      setStageAgingLoading(true);
      setStageAgingError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardStageAging(apiFilter(filters))
      );
      if (isApiSuccess(res)) setStageAgingData(res.data);
      else {
        setStageAgingData(null);
        setStageAgingError(getApiErrorMessage(res, "دریافت ماتریس کهنگی ناموفق بود."));
      }
    } catch {
      setStageAgingData(null);
      setStageAgingError("خطا در دریافت ماتریس کهنگی مراحل.");
    } finally {
      setStageAgingLoading(false);
    }
  }, [apiFilter]);

  const loadCriticalCases = useCallback(async (filters) => {
    try {
      setCriticalCasesLoading(true);
      setCriticalCasesError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardCriticalCases({
          ...apiFilter(filters),
          minDaysInStage: filters?.minDaysInStage ?? 7,
          sortBy: filters?.sortBy || "DaysInStage",
          sortDirection: filters?.sortDirection ?? 2,
          pageNumber: filters?.pageNumber ?? 1,
          pageSize: filters?.pageSize ?? 25,
          operationalStageCode: filters?.operationalStageCode || undefined,
        })
      );
      if (isApiSuccess(res)) setCriticalCasesData(res.data);
      else {
        setCriticalCasesData(null);
        setCriticalCasesError(getApiErrorMessage(res, "دریافت پرونده‌های بحرانی ناموفق بود."));
      }
    } catch {
      setCriticalCasesData(null);
      setCriticalCasesError("خطا در دریافت پرونده‌های بحرانی.");
    } finally {
      setCriticalCasesLoading(false);
    }
  }, [apiFilter]);

  const loadContractorPerformance = useCallback(async (filters) => {
    try {
      setContractorPerfLoading(true);
      setContractorPerfError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardContractorPerformance(apiFilter(filters))
      );
      if (isApiSuccess(res)) setContractorPerfData(res.data);
      else {
        setContractorPerfData(null);
        setContractorPerfError(getApiErrorMessage(res, "دریافت عملکرد پیمانکاران ناموفق بود."));
      }
    } catch {
      setContractorPerfData(null);
      setContractorPerfError("خطا در دریافت عملکرد پیمانکاران.");
    } finally {
      setContractorPerfLoading(false);
    }
  }, [apiFilter]);

  const loadLoanPartnerPerformance = useCallback(async (filters) => {
    try {
      setLoanPartnerPerfLoading(true);
      setLoanPartnerPerfError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardLoanPartnerPerformance(apiFilter(filters))
      );
      if (isApiSuccess(res)) setLoanPartnerPerfData(res.data);
      else {
        setLoanPartnerPerfData(null);
        setLoanPartnerPerfError(getApiErrorMessage(res, "دریافت عملکرد لندتک ناموفق بود."));
      }
    } catch {
      setLoanPartnerPerfData(null);
      setLoanPartnerPerfError("خطا در دریافت عملکرد ارائه‌دهندگان وام.");
    } finally {
      setLoanPartnerPerfLoading(false);
    }
  }, [apiFilter]);

  const loadOperationalBottlenecks = useCallback(async (filters) => {
    try {
      setBottlenecksLoading(true);
      setBottlenecksError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardOperationalBottlenecks(apiFilter(filters))
      );
      if (isApiSuccess(res)) setBottlenecksData(res.data);
      else {
        setBottlenecksData(null);
        setBottlenecksError(getApiErrorMessage(res, "دریافت گلوگاه‌های عملیاتی ناموفق بود."));
      }
    } catch {
      setBottlenecksData(null);
      setBottlenecksError("خطا در دریافت گلوگاه‌های عملیاتی.");
    } finally {
      setBottlenecksLoading(false);
    }
  }, [apiFilter]);

  const loadSlaPerformance = useCallback(async (filters) => {
    try {
      setSlaLoading(true);
      setSlaError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardSlaPerformance(apiFilter(filters))
      );
      if (isApiSuccess(res)) setSlaData(res.data);
      else {
        setSlaData(null);
        setSlaError(getApiErrorMessage(res, "دریافت عملکرد SLA ناموفق بود."));
      }
    } catch {
      setSlaData(null);
      setSlaError("خطا در دریافت عملکرد SLA.");
    } finally {
      setSlaLoading(false);
    }
  }, [apiFilter]);

  const loadHealthScore = useCallback(async (filters) => {
    try {
      setHealthScoreLoading(true);
      setHealthScoreError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardHealthScore(apiFilter(filters))
      );
      if (isApiSuccess(res)) setHealthScoreData(res.data);
      else {
        setHealthScoreData(null);
        setHealthScoreError(getApiErrorMessage(res, "دریافت امتیاز سلامت ناموفق بود."));
      }
    } catch {
      setHealthScoreData(null);
      setHealthScoreError("خطا در دریافت امتیاز سلامت مدیریت.");
    } finally {
      setHealthScoreLoading(false);
    }
  }, [apiFilter]);

  const loadActionCenter = useCallback(async (filters) => {
    try {
      setActionCenterLoading(true);
      setActionCenterError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardActionCenter(apiFilter(filters))
      );
      if (isApiSuccess(res)) setActionCenterData(res.data);
      else {
        setActionCenterData(null);
        setActionCenterError(getApiErrorMessage(res, "دریافت مرکز اقدام ناموفق بود."));
      }
    } catch {
      setActionCenterData(null);
      setActionCenterError("خطا در دریافت مرکز اقدام.");
    } finally {
      setActionCenterLoading(false);
    }
  }, [apiFilter]);

  const loadRequestRiskPredictions = useCallback(async (filters) => {
    try {
      setRiskLoading(true);
      setRiskError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardRequestRiskPredictions(apiFilter(filters), 50)
      );
      if (isApiSuccess(res)) setRiskData(res.data);
      else {
        setRiskData(null);
        setRiskError(getApiErrorMessage(res, "دریافت پیش‌بینی ریسک ناموفق بود."));
      }
    } catch {
      setRiskData(null);
      setRiskError("خطا در دریافت پیش‌بینی ریسک.");
    } finally {
      setRiskLoading(false);
    }
  }, [apiFilter]);

  const loadWorkflowActions = useCallback(async (filters) => {
    try {
      setWorkflowLoading(true);
      setWorkflowError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardActions({ ...apiFilter(filters), openOnly: true })
      );
      if (isApiSuccess(res)) setWorkflowData(res.data);
      else {
        setWorkflowData(null);
        setWorkflowError(getApiErrorMessage(res, "دریافت اقدامات گردش کار ناموفق بود."));
      }
    } catch {
      setWorkflowData(null);
      setWorkflowError("خطا در دریافت اقدامات گردش کار.");
    } finally {
      setWorkflowLoading(false);
    }
  }, [apiFilter]);

  const syncWorkflowActions = useCallback(async (filters) => {
    try {
      setWorkflowSyncing(true);
      const res = await syncManagementDashboardActions(apiFilter(filters));
      if (isApiSuccess(res)) {
        await loadWorkflowActions(filters);
        return res;
      }
      throw new Error(getApiErrorMessage(res, "همگام‌سازی اقدامات ناموفق بود."));
    } finally {
      setWorkflowSyncing(false);
    }
  }, [apiFilter, loadWorkflowActions]);

  const updateWorkflowAction = useCallback(async (payload, filters) => {
    const res = await updateManagementDashboardAction(payload);
    if (isApiSuccess(res)) {
      await loadWorkflowActions(filters);
      return res.data;
    }
    throw new Error(getApiErrorMessage(res, "به‌روزرسانی اقدام ناموفق بود."));
  }, [loadWorkflowActions]);

  const loadExecutiveBrief = useCallback(async (filters) => {
    try {
      setBriefLoading(true);
      setBriefError(null);
      const res = await fetchDashboardGet(() =>
        getManagementDashboardExecutiveDailyBrief(apiFilter(filters))
      );
      if (isApiSuccess(res)) setBriefData(res.data);
      else {
        setBriefData(null);
        setBriefError(getApiErrorMessage(res, "دریافت خلاصه روزانه ناموفق بود."));
      }
    } catch {
      setBriefData(null);
      setBriefError("خطا در دریافت خلاصه روزانه اجرایی.");
    } finally {
      setBriefLoading(false);
    }
  }, [apiFilter]);

  const loadBenchmarkComparison = useCallback(async (filters, dimensionType = "Province") => {
    try {
      setBenchmarkLoading(true);
      setBenchmarkError(null);
      const cacheKey = `benchmark:${dimensionType}:${JSON.stringify(apiFilter(filters))}`;
      const cached = cacheRef.current.get(cacheKey);
      if (cached && Date.now() - cached.at < DASHBOARD_CACHE_TTL_MS) {
        setBenchmarkData(cached.data);
        setBenchmarkLoading(false);
        return;
      }
      const res = await fetchDashboardGet(() =>
        getManagementDashboardBenchmarkComparison(apiFilter(filters), dimensionType)
      );
      if (isApiSuccess(res)) {
        setBenchmarkData(res.data);
        cacheRef.current.set(cacheKey, { at: Date.now(), data: res.data });
      } else {
        setBenchmarkData(null);
        setBenchmarkError(getApiErrorMessage(res, "دریافت بنچمارک ناموفق بود."));
      }
    } catch {
      setBenchmarkData(null);
      setBenchmarkError("خطا در دریافت مقایسه بنچمارک.");
    } finally {
      setBenchmarkLoading(false);
    }
  }, [apiFilter]);

  const loadDashboardAccess = useCallback(async () => {
    try {
      const res = await fetchDashboardGet(() => getManagementDashboardAccess());
      if (isApiSuccess(res)) setDashboardAccess(buildClientDashboardAccess(res.data));
      else setDashboardAccess(buildClientDashboardAccess(null));
    } catch {
      setDashboardAccess(buildClientDashboardAccess(null));
    }
  }, []);

  const loaderByKey = useMemo(
    () => ({
      executiveKpis: loadExecutiveKpis,
      alerts: loadAlerts,
      healthScore: loadHealthScore,
      executiveBrief: loadExecutiveBrief,
      benchmark: (filters) => loadBenchmarkComparison(filters, benchmarkDimension),
      funnel: loadFunnel,
      readiness: loadReadiness,
      stageAging: loadStageAging,
      bottlenecks: loadOperationalBottlenecks,
      criticalCases: loadCriticalCases,
      trends: loadTrends,
      contractorPerformance: loadContractorPerformance,
      acquisition: loadAcquisitionBreakdown,
      lending: loadLending,
      mehrsan: loadMehrsan,
      loanPartnerPerformance: loadLoanPartnerPerformance,
      actionCenter: loadActionCenter,
      workflowActions: loadWorkflowActions,
      sla: loadSlaPerformance,
      riskPredictions: loadRequestRiskPredictions,
    }),
    [
      loadExecutiveKpis,
      loadAlerts,
      loadHealthScore,
      loadExecutiveBrief,
      loadBenchmarkComparison,
      benchmarkDimension,
      loadFunnel,
      loadReadiness,
      loadStageAging,
      loadOperationalBottlenecks,
      loadCriticalCases,
      loadTrends,
      loadContractorPerformance,
      loadAcquisitionBreakdown,
      loadLending,
      loadMehrsan,
      loadLoanPartnerPerformance,
      loadActionCenter,
      loadWorkflowActions,
      loadSlaPerformance,
      loadRequestRiskPredictions,
    ]
  );

  const activeTab = useMemo(() => {
    const tab = searchParams.get("tab");
    return Object.values(MANAGEMENT_DASHBOARD_TABS).includes(tab)
      ? tab
      : DEFAULT_MANAGEMENT_TAB;
  }, [searchParams]);

  const loadTabData = useCallback(
    async (tabId, filters, { force = false } = {}) => {
      const keys = MANAGEMENT_TAB_LOADER_KEYS[tabId] || [];
      const tasks = [];
      keys.forEach((key) => {
        const already = loadedKeysRef.current.has(key);
        if (!force && already) return;
        const loader = loaderByKey[key];
        if (!loader) return;
        tasks.push(
          Promise.resolve(loader(filters)).then(() => {
            loadedKeysRef.current.add(key);
          })
        );
      });
      if (tasks.length) await Promise.all(tasks);
      loadedTabsRef.current.add(tabId);
    },
    [loaderByKey]
  );

  const reloadDashboard = useCallback(
    async (filters) => {
      loadedKeysRef.current.clear();
      loadedTabsRef.current.clear();
      cacheRef.current.clear();
      await Promise.all([
        loadDashboardAccess(),
        loadTabData(activeTab || DEFAULT_MANAGEMENT_TAB, filters, { force: true }),
        loadAnalytics(periodDays),
      ]);
    },
    [activeTab, loadDashboardAccess, loadTabData, loadAnalytics, periodDays]
  );

  useEffect(() => {
    if (!isBootstrapReady) return undefined;

    const seq = ++loadSeqRef.current;
    let cancelled = false;

    const run = async () => {
      await delay(DASHBOARD_BOOTSTRAP_DELAY_MS);
      if (cancelled || seq !== loadSeqRef.current) return;
      loadedKeysRef.current.clear();
      loadedTabsRef.current.clear();
      cacheRef.current.clear();
      await loadDashboardAccess();
      if (cancelled || seq !== loadSeqRef.current) return;
      await loadTabData(activeTab, dashboardFilters, { force: true });
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isBootstrapReady, dashboardFilters, loadDashboardAccess, loadTabData, activeTab]);

  useEffect(() => {
    if (!isBootstrapReady) return undefined;
    if (loadedTabsRef.current.has(activeTab) && loadedKeysRef.current.size > 0) {
      // Tab already visited with current filter set — still fill any missing keys
      loadTabData(activeTab, dashboardFilters, { force: false });
      return undefined;
    }
    loadTabData(activeTab, dashboardFilters, { force: false });
    return undefined;
  }, [isBootstrapReady, activeTab, dashboardFilters, loadTabData]);

  useEffect(() => {
    if (!isBootstrapReady) return undefined;
    if (activeTab !== MANAGEMENT_DASHBOARD_TABS.EXECUTIVE) return undefined;
    loadBenchmarkComparison(dashboardFilters, benchmarkDimension);
  }, [isBootstrapReady, activeTab, benchmarkDimension, dashboardFilters, loadBenchmarkComparison]);

  useEffect(() => {
    if (!isBootstrapReady) return undefined;

    let cancelled = false;

    const run = async () => {
      await delay(DASHBOARD_BOOTSTRAP_DELAY_MS);
      if (cancelled) return;
      await loadAnalytics(periodDays);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isBootstrapReady, periodDays, loadAnalytics]);

  const handleTrendGroupChange = useCallback(
    (group) => {
      const next = new URLSearchParams(searchParams);
      next.set("trendGroup", group);
      const defaultMetric = TREND_METRIC_GROUPS[group]?.codes?.[0];
      if (defaultMetric) next.set("trendMetric", defaultMetric);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleTrendMetricChange = useCallback(
    (metricCode) => {
      const next = new URLSearchParams(searchParams);
      next.set("trendMetric", metricCode);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const resolvedTrendMetric =
    selectedTrendMetric
    || TREND_METRIC_GROUPS[metricGroup]?.codes?.find((code) =>
      trendSeries.some((s) => pick(s, "code", "Code") === code)
    )
    || TREND_METRIC_GROUPS[metricGroup]?.codes?.[0]
    || "";

  return {
    dashboardFilters,
    handleBootstrapReady,
    kpiLoading,
    reloadDashboard,
    loadAlerts,
    loadExecutiveKpis,
    loadReadiness,
    loadFunnel,
    loadLending,
    loadMehrsan,
    loadTrends,
    loadAcquisitionBreakdown,
    loadStageAging,
    loadCriticalCases,
    loadContractorPerformance,
    loadLoanPartnerPerformance,
    loadOperationalBottlenecks,
    loadSlaPerformance,
    loadHealthScore,
    loadActionCenter,
    loadRequestRiskPredictions,
    loadWorkflowActions,
    syncWorkflowActions,
    updateWorkflowAction,
    loadExecutiveBrief,
    loadBenchmarkComparison,
    setBenchmarkDimension,
    analyticsLoading,
    kpiError,
    alertsLoading,
    alertsError,
    alertsData,
    readinessLoading,
    readinessError,
    readinessData,
    funnelLoading,
    funnelError,
    funnelStages,
    funnelMeta,
    lendingLoading,
    lendingError,
    lendingData,
    mehrsanLoading,
    mehrsanError,
    mehrsanData,
    trendLoading,
    trendError,
    growthAnalytics,
    smsSummary,
    overview,
    executiveKpis,
    trendSeries,
    acquisitionLoading,
    acquisitionError,
    acquisitionData,
    stageAgingLoading,
    stageAgingError,
    stageAgingData,
    criticalCasesLoading,
    criticalCasesError,
    criticalCasesData,
    contractorPerfLoading,
    contractorPerfError,
    contractorPerfData,
    loanPartnerPerfLoading,
    loanPartnerPerfError,
    loanPartnerPerfData,
    bottlenecksLoading,
    bottlenecksError,
    bottlenecksData,
    slaLoading,
    slaError,
    slaData,
    healthScoreLoading,
    healthScoreError,
    healthScoreData,
    actionCenterLoading,
    actionCenterError,
    actionCenterData,
    riskLoading,
    riskError,
    riskData,
    workflowLoading,
    workflowError,
    workflowData,
    workflowSyncing,
    briefLoading,
    briefError,
    briefData,
    benchmarkLoading,
    benchmarkError,
    benchmarkData,
    benchmarkDimension,
    dashboardAccess,
    currencyUnit,
    periodDays,
    setPeriodDays,
    metricGroup,
    resolvedTrendMetric,
    handleTrendGroupChange,
    handleTrendMetricChange,
  };
};

export default useManagementDashboardData;
